import { useSelector } from 'react-redux';
import { IOysterLineDetail } from '../../entities/farms.entities';
import {
  ICycleResource,
  IOysterSeeding,
} from '../../entities/growing.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { Subtitle } from '../shared';
import { translate } from '../../lib/lang.helper';
import { formatNumber } from '../../entities/util-functions';
import {
  defaultDateFormat,
  displayValueUnit,
  pcsToDoz,
} from '../../lib/common.helpers';
import {
  LineInventoriesLabel,
  SeedInventoriesLabel,
  StageLabelSpan,
} from '../CommonViews';
import { selectAccount } from '../../store/auth/auth.selector';
import { getOysterHeaderLabel } from './LineHeadersModal';
import { getLineGrowingData } from '../../lib/farm.helpers';

interface Props {
  line: IOysterLineDetail;
  cycle: ICycleResource | null;
}

const OysterLineHeaders = ({ line, cycle }: Props) => {
  const lang = useSelector(selectLang);
  const accountSetting = useSelector(selectAccount);
  const unitType = accountSetting?.display_unit;
  const oysterLineHeaders = accountSetting?.oyster_line_headers ?? [];

  const oysterSeedings =
    cycle?.crop_type === 'OYSTER' ? (cycle.seedings as IOysterSeeding[]) : [];
  const growingBaskets = line.growing_cycle
    ? line.growing_cycle.current_basket_count
    : 0;
  const basketSum = oysterSeedings.reduce((p, c) => p + c.basket_count, 0);
  const growingData = getLineGrowingData(line);

  const headers = [
    {
      key: 'current_basket_count',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('current_basket_count', lang, line)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {line.growing_cycle
              ? `${growingBaskets}/${line.basket_count} (${formatNumber(
                  (growingBaskets * 100) / line.basket_count,
                )}%)`
              : `-/${line.basket_count}`}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'backbone',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('backbone', lang, line)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(line.backbone, unitType, 'distance_m')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'planned_date_seed',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('planned_date_seed', lang, line)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(cycle?.main_seed?.planned_date_seed, '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'shell_size',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('shell_size', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              growingData.shell_size_avg,
              unitType,
              'size_mm',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'planned_date_harvest',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('planned_date_harvest', lang, line)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(growingData.planned_date_harvest, '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'spat_seed_type',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('spat_seed_type', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {cycle?.main_seed?.spat_storage?.seed_type ?? '-'}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'stage',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('stage', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {basketSum > 0 &&
              (oysterSeedings.length === 1 ? (
                <div>
                  {translate(lang, 'seed stage')}
                  <span> </span>
                  <StageLabelSpan
                    stage={oysterSeedings[0].stage}
                    farmId={line.farm_id}
                  />
                </div>
              ) : (
                oysterSeedings.map((x, i) => (
                  <div key={i}>
                    {`${formatNumber(
                      (x.basket_count / basketSum) * 100,
                    )}% ${translate(lang, 'seed stage')} `}
                    <StageLabelSpan stage={x.stage} farmId={line.farm_id} />
                  </div>
                ))
              ))}
            {cycle?.harvests.length === 1 ? (
              <div>
                {translate(lang, 'harvest stage')}
                <span> </span>
                <StageLabelSpan stage={1} farmId={line.farm_id} />
              </div>
            ) : (
              cycle?.harvests.map((x, i) => {
                if (x.line_length) {
                  return (
                    <div key={i}>
                      {`${formatNumber(
                        (x.line_length / basketSum) * 100,
                      )}% ${translate(lang, 'harvest stage')} `}
                      <StageLabelSpan stage={i + 1} farmId={line.farm_id} />
                    </div>
                  );
                }
              })
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'inventories',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('inventories', lang)}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {cycle
              ? SeedInventoriesLabel(cycle.inventories_sum)
              : LineInventoriesLabel(line.pending_inventories ?? [])}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'predicted_amount',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('predicted_amount', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {line.growing_cycle?.prediction?.harvest_amount
              ? displayValueUnit(
                  pcsToDoz(line.growing_cycle?.prediction?.harvest_amount),
                  unitType,
                  'dz',
                )
              : '-'}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'predicted_date',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('predicted_date', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {defaultDateFormat(
              line.growing_cycle?.prediction?.harvest_date,
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'line_fill_percent',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {getOysterHeaderLabel('line_fill_percent', lang)}
          </Subtitle>
          <Subtitle size={5} color='black' align='left' fontWeight={500}>
            {displayValueUnit(line.fill_percent, unitType, '%', '-')}
          </Subtitle>
        </>
      ),
    },
    {
      key: 'spacing',
      component: (
        <>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {translate(lang, 'Spacing')}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {displayValueUnit(
              cycle?.main_seed.spacing,
              unitType,
              'size_mm',
              '-',
            )}
          </Subtitle>
        </>
      ),
    },
  ].filter(x => {
    if (!line.growing_cycle) {
      if (
        [
          'planned_date_seed',
          'shell_size',
          'planned_date_harvest',
          'spat_seed_type',
          'stage',
          'density',
          'predicted_amount',
          'predicted_date',
          'spacing',
          'drop',
        ].includes(x.key)
      )
        return false;
    }
    return oysterLineHeaders.includes(x.key);
  });

  return (
    <div className='line-headers'>
      {headers.map(h => (
        <div key={h.key}>{h.component}</div>
      ))}
    </div>
  );
};

export default OysterLineHeaders;
